import { useState } from "react";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import {
  configureRateModeThunk,
  configureHybridPolicyThunk,
} from "../../../../../store/thunks/branchThunks";

const useRateConfig = (branch, setLoading) => {
  const dispatch = useDispatch();

  const [rateConfig, setRateConfig] = useState({
    ratePolicy: branch.rateConfig?.ratePolicy || "INHERIT",
    rateMode: branch.rateConfig?.rateMode || "ADMIN",
    inheritFromBranch: branch.rateConfig?.inheritFromBranch || "",

    // Hybrid configuration
    hybridConfig: {
      metalPolicies: {
        gold: branch.rateConfig?.hybridConfig?.metalPolicies?.gold || "INHERIT",
        silver:
          branch.rateConfig?.hybridConfig?.metalPolicies?.silver || "INHERIT",
        platinum:
          branch.rateConfig?.hybridConfig?.metalPolicies?.platinum || "INHERIT",
        diamond:
          branch.rateConfig?.hybridConfig?.metalPolicies?.diamond || "CUSTOM",
      },
      inheritBase: branch.rateConfig?.hybridConfig?.inheritBase !== false,
      customizeMargins:
        branch.rateConfig?.hybridConfig?.customizeMargins || false,
    },

    // Fallback configuration
    fallbackConfig: {
      fallbackToMain:
        branch.rateConfig?.fallbackConfig?.fallbackToMain !== false,
      fallbackToLive:
        branch.rateConfig?.fallbackConfig?.fallbackToLive || false,
      fallbackOrder: branch.rateConfig?.fallbackConfig?.fallbackOrder || [
        "MAIN",
        "LIVE",
      ],
    },

    // Auto sync
    autoSync: {
      enabled: branch.rateConfig?.autoSync?.enabled || false,
      frequency: branch.rateConfig?.autoSync?.frequency || "DAILY",
      lastSyncAt: branch.rateConfig?.autoSync?.lastSyncAt || null,
      nextSyncAt: branch.rateConfig?.autoSync?.nextSyncAt || null,
    },

    // Adjustments
    adjustments: {
      transport: branch.rateConfig?.adjustments?.transport || 0,
      operational: branch.rateConfig?.adjustments?.operational || 0,
      rent: branch.rateConfig?.adjustments?.rent || 0,
      margin: branch.rateConfig?.adjustments?.margin || 0,
    },

    // Margin overrides
    marginOverrides: {
      gold: {
        sellMargin: branch.rateConfig?.marginOverrides?.gold?.sellMargin || 0,
        buyMargin: branch.rateConfig?.marginOverrides?.gold?.buyMargin || 0,
      },
      silver: {
        sellMargin: branch.rateConfig?.marginOverrides?.silver?.sellMargin || 0,
        buyMargin: branch.rateConfig?.marginOverrides?.silver?.buyMargin || 0,
      },
      platinum: {
        sellMargin:
          branch.rateConfig?.marginOverrides?.platinum?.sellMargin || 0,
        buyMargin: branch.rateConfig?.marginOverrides?.platinum?.buyMargin || 0,
      },
      diamond: {
        margin: branch.rateConfig?.marginOverrides?.diamond?.margin || 0,
      },
    },
  });

  const updateMarginOverride = (metal, type, value) => {
    setRateConfig((prev) => ({
      ...prev,
      marginOverrides: {
        ...prev.marginOverrides,
        [metal]: {
          ...prev.marginOverrides[metal],
          [type]: value,
        },
      },
    }));
  };

  const updateHybridMetalPolicy = (metal, policy) => {
    setRateConfig((prev) => ({
      ...prev,
      hybridConfig: {
        ...prev.hybridConfig,
        metalPolicies: {
          ...prev.hybridConfig.metalPolicies,
          [metal]: policy,
        },
      },
    }));
  };

  const updateFallbackOrder = (newOrder) => {
    setRateConfig((prev) => ({
      ...prev,
      fallbackConfig: {
        ...prev.fallbackConfig,
        fallbackOrder: newOrder,
      },
    }));
  };

  const handleSave = async (branchId) => {
    setLoading(true);
    try {
      const configData = {
        ratePolicy: rateConfig.ratePolicy,
        rateMode: rateConfig.rateMode,
        inheritFromBranch: rateConfig.inheritFromBranch,
        fallbackConfig: rateConfig.fallbackConfig,
        autoSync: rateConfig.autoSync,
        adjustments: {
          transport: Number(rateConfig.adjustments.transport) || 0,
          operational: Number(rateConfig.adjustments.operational) || 0,
          rent: Number(rateConfig.adjustments.rent) || 0,
          margin: Number(rateConfig.adjustments.margin) || 0,
        },
        marginOverrides: {
          gold: {
            sellMargin: Number(rateConfig.marginOverrides.gold.sellMargin) || 0,
            buyMargin: Number(rateConfig.marginOverrides.gold.buyMargin) || 0,
          },
          silver: {
            sellMargin:
              Number(rateConfig.marginOverrides.silver.sellMargin) || 0,
            buyMargin: Number(rateConfig.marginOverrides.silver.buyMargin) || 0,
          },
          platinum: {
            sellMargin:
              Number(rateConfig.marginOverrides.platinum.sellMargin) || 0,
            buyMargin:
              Number(rateConfig.marginOverrides.platinum.buyMargin) || 0,
          },
          diamond: {
            margin: Number(rateConfig.marginOverrides.diamond.margin) || 0,
          },
        },
      };

      if (rateConfig.ratePolicy === "HYBRID") {
        await dispatch(
          configureHybridPolicyThunk({
            branchId,
            hybridConfig: rateConfig.hybridConfig,
          })
        ).unwrap();

        await dispatch(
          configureRateModeThunk({
            branchId,
            rateConfig: configData,
          })
        ).unwrap();
      } else {
        await dispatch(
          configureRateModeThunk({
            branchId,
            rateConfig: configData,
          })
        ).unwrap();
      }

      toast.success("Rate configuration saved successfully");
    } catch (error) {
      console.error("Failed to save rate configuration:", error);
      toast.error("Failed to save configuration");
    } finally {
      setLoading(false);
    }
  };

  return {
    rateConfig,
    setRateConfig,
    updateMarginOverride,
    updateHybridMetalPolicy,
    updateFallbackOrder,
    handleSave,
  };
};

export default useRateConfig;
